import React from "react";
import Div from "@jumbo/shared/Div";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import LoadingButton from "@mui/lab/LoadingButton";
import { useTranslation } from "react-i18next";

import { useAppMutation } from "../../../../../services";
import parentServices from "../../../../../services/parent-services";
import LinearProgress from "@mui/material/LinearProgress";

import {
  COUNTRIES,
  findCountryByCode,
  COUNTRIES_I18N,
} from "../../../../../utils/constants/appActions";

const LoadingAdornment = ({ loading }) =>
  loading ? (
    <LinearProgress
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: 2,
        borderRadius: 1,
      }}
    />
  ) : null;

/* Label component */
const FieldLabel = ({ children, required }) => (
  <Typography
    sx={{
      fontFamily: "Nunito, sans-serif",
      fontSize: "14px",
      fontWeight: 700,
      color: "rgb(0, 36, 101)",
      mb: 0.8,
    }}
  >
    {children} {required ? "*" : ""}
  </Typography>
);

const AddressDetails = ({ hide, next, back }) => {
  const { t, i18n } = useTranslation();
  const isFr = (i18n.language || "").toLowerCase().startsWith("fr");

  const getCountryLabel = React.useCallback(
    (option) => {
      if (!option) return "";
      if (typeof option === "string") return option;

      // prefer i18n label, fallback to label/code
      return isFr
        ? option.label_fr ||
            option.label_en ||
            option.label ||
            option.code ||
            ""
        : option.label_en ||
            option.label_fr ||
            option.label ||
            option.code ||
            "";
    },
    [isFr],
  );

  const clearErrTimerRef = React.useRef(null);

  const {
    mutate: loadProfile,
    isSuccess: isLoadSuccess,
    data: profile,
    isError: isLoadError,
    isLoading: isProfileLoading,
  } = useAppMutation(parentServices.loadProfile);

  const [values, setValues] = React.useState({
    address1: "",
    address2: "",
    country: null,
    city: "",
    postalCode: "",
  });

  const [errMsg, setErrMsg] = React.useState("");
  const [countryRefresh, setCountryRefresh] = React.useState(false);

  // ✅ Field-level validation errors (UI only; no business logic changes)
  const [fieldErrors, setFieldErrors] = React.useState({
    address1: "",
    city: "",
    postalCode: "",
    country: "",
  });

  // ✅ needed for freeSolo input text control (typing a new country)
  const [countryInput, setCountryInput] = React.useState("");

  React.useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (isLoadSuccess && profile) {
      const location =
        profile?.location ||
        profile?.profile?.location ||
        profile?.data?.location ||
        null;

      if (location) {
        const countryObjOld =
          typeof location.country === "string"
            ? findCountryByCode(location.country)
            : location.country;

        const countryObj =
          typeof location.country === "string"
            ? COUNTRIES_I18N.find((c) => c.code === location.country) ||
              findCountryByCode(location.country)
            : location.country;

        setValues({
          address1: location.address1 || "",
          address2: location.address2 || "",
          country: countryObj || null,
          city: location.city || "",
          postalCode: location.postalCode || "",
        });

        // keep input text aligned with loaded value
        setCountryInput(getCountryLabel(countryObj) || countryObj?.code || "");
      }

      setCountryRefresh(true);
    }

    if (isLoadError) {
      setCountryRefresh(true);
    }
  }, [isLoadSuccess, isLoadError, profile, i18n.language]);

  const handleChange = (prop) => (event) => {
    setErrMsg("");
    setFieldErrors((prev) => ({ ...prev, [prop]: "" }));
    setValues((prev) => ({ ...prev, [prop]: event.target.value }));
  };

  // ✅ Updated: supports selecting OR typing a new country
  const handleCountryChange = (event, newValue) => {
    setErrMsg("");
    setFieldErrors((prev) => ({ ...prev, country: "" }));

    // freeSolo => newValue can be string
    if (typeof newValue === "string") {
      const typed = newValue.trim();
      setValues((prev) => ({
        ...prev,
        country: typed ? { code: typed, label: typed } : null,
      }));
      return;
    }

    // normal option object or null
    setValues((prev) => ({ ...prev, country: newValue || null }));
  };

  const saveAddress = () => {
    setErrMsg("");

    const nextErrors = {
      address1: "",
      city: "",
      postalCode: "",
      country: "",
    };

    if (!values.address1?.trim()) {
      nextErrors.address1 = t("common.required", "Required");
    }
    if (!values.city?.trim()) {
      nextErrors.city = t("common.required", "Required");
    }
    if (!values.postalCode?.trim()) {
      nextErrors.postalCode = t("common.required", "Required");
    }
    if (!values?.country) {
      nextErrors.country = t("common.required", "Required");
    }

    const hasError = Object.values(nextErrors).some(Boolean);

    if (hasError) {
      setFieldErrors(nextErrors);
      setErrMsg(t("common.inputError", "Please fill required fields"));
      if (clearErrTimerRef.current) clearTimeout(clearErrTimerRef.current);
      clearErrTimerRef.current = setTimeout(() => {
        setFieldErrors({
          address1: "",
          city: "",
          postalCode: "",
          country: "",
        });
      }, 3000);
      return;
    }

    next({
      address1: values.address1?.trim() || "",
      address2: values.address2?.trim() || "",
      country: values.country?.code || "",
      city: values.city?.trim() || "",
      postalCode: values.postalCode?.trim() || "",
      countryObj: values.country || null,
    });
  };

  return (
    <Div
      sx={{
        width: "100%",
        backgroundColor: "rgb(255, 255, 255)",
        borderRadius: "18px",
        px: { xs: 3, sm: 4, md: 3 },
        pt: 3,
        pb: 3,
        minHeight: "auto",
      }}
    >
      {/* Title */}
      <Typography
        sx={{
          display: "block",
          fontFamily: "Nunito, sans-serif",
          fontSize: "20px",
          fontWeight: 800,
          color: "rgb(0, 36, 101)",
          textAlign: "center",
          mb: 2,
        }}
      >
        {t("payment-modal.addressTitle")}
      </Typography>

      <Divider sx={{ mb: 3, borderColor: "rgb(229, 231, 235)" }} />

      {/* Info box */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "10px",
          border: "1px solid rgb(229, 231, 235)",
          p: 1.6,
          mb: 2.5,
          backgroundColor: "rgb(229, 229, 229)",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Nunito, sans-serif",
            fontSize: 12.5,
            color: "rgb(107, 114, 128)",
            lineHeight: 1.4,
          }}
        >
          {t("payment-modal.addressSubTitle")}
        </Typography>
      </Paper>

      {/* Form */}
      <FormControl noValidate autoComplete="off" sx={{ width: "100%" }}>
        <Stack spacing={2}>
          <Div>
            <FieldLabel required>{t("payment-modal.address")}</FieldLabel>
            <TextField
              fullWidth
              placeholder="123 Main Street"
              value={values.address1}
              onChange={handleChange("address1")}
              disabled={isProfileLoading}
              error={!!fieldErrors.address1}
              helperText={fieldErrors.address1}
              FormHelperTextProps={{
                sx: { fontFamily: "Nunito, sans-serif" },
              }}
              InputProps={{
                endAdornment: <LoadingAdornment loading={isProfileLoading} />,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  height: 44,
                  backgroundColor: "rgb(255, 255, 255)",
                  fontFamily: "Nunito, sans-serif",
                },
              }}
            />
          </Div>

          <Div>
            <FieldLabel>{t("payment-modal.address2")}</FieldLabel>
            <TextField
              fullWidth
              placeholder="Apartment, floor, etc."
              value={values.address2}
              onChange={handleChange("address2")}
              InputProps={{
                endAdornment: <LoadingAdornment loading={isProfileLoading} />,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  height: 44,
                  backgroundColor: "rgb(255, 255, 255)",
                  fontFamily: "Nunito, sans-serif",
                },
              }}
            />
          </Div>

          <Div sx={{ display: "flex", gap: 2 }}>
            <Div sx={{ flex: 1 }}>
              <FieldLabel required>{t("payment-modal.city")}</FieldLabel>
              <TextField
                fullWidth
                placeholder="New York"
                value={values.city}
                onChange={handleChange("city")}
                error={!!fieldErrors.city}
                helperText={fieldErrors.city}
                FormHelperTextProps={{
                  sx: { fontFamily: "Nunito, sans-serif" },
                }}
                InputProps={{
                  endAdornment: <LoadingAdornment loading={isProfileLoading} />,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    height: 44,
                    backgroundColor: "rgb(255, 255, 255)",
                    fontFamily: "Nunito, sans-serif",
                  },
                }}
              />
            </Div>

            <Div sx={{ flex: 1 }}>
              <FieldLabel required>{t("payment-modal.postalCode")}</FieldLabel>
              <TextField
                fullWidth
                placeholder="10001"
                value={values.postalCode}
                onChange={handleChange("postalCode")}
                error={!!fieldErrors.postalCode}
                helperText={fieldErrors.postalCode}
                FormHelperTextProps={{
                  sx: { fontFamily: "Nunito, sans-serif" },
                }}
                InputProps={{
                  endAdornment: <LoadingAdornment loading={isProfileLoading} />,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    height: 44,
                    backgroundColor: "rgb(255, 255, 255)",
                    fontFamily: "Nunito, sans-serif",
                  },
                }}
              />
            </Div>
          </Div>

          <Div>
            <FieldLabel required>{t("payment-modal.country")}</FieldLabel>

            {countryRefresh && (
              <Autocomplete
                options={COUNTRIES_I18N}
                value={values.country}
                freeSolo
                inputValue={countryInput}
                onInputChange={(e, v) => setCountryInput(v)}
                onChange={(event, newValue) => {
                  setErrMsg("");
                  setFieldErrors((prev) => ({ ...prev, country: "" }));

                  // freeSolo => newValue can be string
                  if (typeof newValue === "string") {
                    const typed = newValue.trim();
                    setValues((prev) => ({
                      ...prev,
                      country: typed
                        ? {
                            code: typed,
                            label: typed,
                            label_en: typed,
                            label_fr: typed,
                          }
                        : null,
                    }));
                    return;
                  }

                  setValues((prev) => ({ ...prev, country: newValue || null }));
                }}
                getOptionLabel={(option) => getCountryLabel(option)}
                isOptionEqualToValue={(option, value) => {
                  // compare by code when possible (best), else by translated label
                  const oc = typeof option === "string" ? option : option?.code;
                  const vc = typeof value === "string" ? value : value?.code;
                  if (oc && vc) return String(oc) === String(vc);

                  return (
                    String(getCountryLabel(option) || "") ===
                    String(getCountryLabel(value) || "")
                  );
                }}
                renderOption={(props, option) => {
                  const opt =
                    typeof option === "string"
                      ? {
                          code: option,
                          label: option,
                          label_en: option,
                          label_fr: option,
                        }
                      : option;

                  const hasIso2 = opt?.code && String(opt.code).length === 2;
                  const label = getCountryLabel(opt);

                  return (
                    <Box
                      component="li"
                      sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                      {...props}
                    >
                      {hasIso2 ? (
                        <img
                          loading="lazy"
                          width="20"
                          src={`https://flagcdn.com/w20/${opt.code.toLowerCase()}.png`}
                          srcSet={`https://flagcdn.com/w40/${opt.code.toLowerCase()}.png 2x`}
                          alt=""
                        />
                      ) : (
                        <Box sx={{ width: 20, mr: 2 }} />
                      )}
                      {label} {hasIso2 ? `(${opt.code})` : ""}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={isFr ? "France" : "France"}
                    error={!!fieldErrors.country}
                    helperText={fieldErrors.country}
                    FormHelperTextProps={{
                      sx: { fontFamily: "Nunito, sans-serif" },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: 44,
                        backgroundColor: "rgb(255, 255, 255)",
                        fontFamily: "Nunito, sans-serif",
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          <LoadingAdornment loading={isProfileLoading} />
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Div>

          {/*isProfileLoading && (
            <Alert severity="info">{t("common.loading", "Loading...")}</Alert>
          )*/}

          {/*errMsg && <Alert severity="error">{errMsg}</Alert>*/}

          <Stack
            direction="row"
            spacing={2}
            sx={{ pt: 1.5, justifyContent: "center" }}
          >
            <Button
              onClick={back || (() => hide(false))}
              sx={{
                flex: 1,
                maxWidth: 260,
                backgroundColor: "rgb(224, 224, 224)",
                borderRadius: "10px",
                fontSize: "14px",
                fontWeight: 600,
                fontFamily: "Nunito, sans-serif",
                color: "rgb(102, 102, 102)",
                height: 44,
                "&:hover": { backgroundColor: "rgb(219, 219, 219)" },
              }}
            >
              {t("payment-modal.back")}
            </Button>

            <LoadingButton
              onClick={saveAddress}
              sx={{
                flex: 1,
                maxWidth: 260,
                borderRadius: "10px",
                backgroundColor: "rgb(29, 106, 222)",
                fontSize: "14px",
                fontWeight: 600,
                fontFamily: "Nunito, sans-serif",
                color: "rgb(255, 255, 255)",
                height: 44,
                "&:hover": { backgroundColor: "rgb(30, 79, 214)" },
              }}
            >
              {t("payment-modal.continue")}
            </LoadingButton>
          </Stack>
        </Stack>
      </FormControl>
    </Div>
  );
};

export default AddressDetails;
